import { Link } from "react-router-dom";
import { Mail, MapPin, Clock, ArrowRight, Shield, MessageCircle } from "lucide-react";
import { Separator } from "@/components/ui/separator";

export default function Contact() {
  return (
    <div className="grid-bg min-h-screen">
      {/* Header */}
      <div
        className="py-20"
        style={{ background: 'var(--bg-surface)', borderBottom: '1px solid rgba(0,240,255,0.08)' }}
      >
        <div className="max-w-7xl mx-auto px-6">
          <span
            className="text-[11px] tracking-[0.2em] uppercase block mb-2"
            style={{ color: 'var(--neon-magenta)', fontFamily: "'Roboto Mono', monospace" }}
          >
            Get in Touch
          </span>
          <h1
            className="text-4xl sm:text-5xl font-bold tracking-tighter mb-4"
            style={{ fontFamily: "'Rajdhani', sans-serif" }}
          >
            Contact Us
          </h1>
          <p className="text-sm max-w-2xl" style={{ color: 'var(--text-secondary)' }}>
            For all product inquiries, pricing requests, bulk orders, and general questions, please reach out to us directly via email.
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-16">
        {/* Main email CTA */}
        <div
          data-testid="contact-email-card"
          className="neon-border rounded-lg p-10 md:p-14 text-center relative overflow-hidden mb-12"
          style={{ background: 'var(--bg-surface)' }}
        >
          <div className="absolute inset-0 opacity-5" style={{ background: 'radial-gradient(circle at 50% 50%, var(--neon-cyan), transparent 70%)' }} />
          <div className="relative">
            <Mail className="w-10 h-10 mx-auto mb-6" style={{ color: 'var(--neon-cyan)' }} />
            <h2
              className="text-2xl sm:text-3xl font-bold tracking-tight mb-3"
              style={{ fontFamily: "'Rajdhani', sans-serif" }}
            >
              Email Us Directly
            </h2>
            <p className="text-sm mb-8 max-w-lg mx-auto" style={{ color: 'var(--text-secondary)' }}>
              Send us an email with your research needs, desired compounds and quantities, and any questions. Our team typically responds within 24-48 business hours.
            </p>
            <a
              href="mailto:info@apollocompounds.com"
              data-testid="contact-email-link"
              className="btn-neon px-10 py-4 rounded text-base inline-flex items-center gap-3"
              style={{ fontFamily: "'Rajdhani', sans-serif", letterSpacing: '0.05em' }}
            >
              info@apollocompounds.com
              <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </div>

        {/* Free consultation */}
        <div
          className="neon-border rounded-lg p-8 md:p-10 mb-12 flex flex-col md:flex-row items-center gap-8"
          style={{ background: 'var(--bg-surface)' }}
        >
          <MessageCircle className="w-10 h-10 flex-shrink-0" style={{ color: 'var(--neon-magenta)' }} />
          <div className="text-center md:text-left flex-1">
            <h3 className="text-xl font-bold mb-2" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
              Free Consultations Available
            </h3>
            <p className="text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
              Not sure which compound is right for your study? We offer free consultations to help you better understand your research needs and provide guidance on selecting the appropriate peptides, dosages, and configurations for your specific application. No obligation, no pressure — just honest support from people who understand the science.
            </p>
          </div>
          <a
            href="mailto:info@apollocompounds.com?subject=Free%20Consultation%20Request"
            className="btn-ghost-neon px-6 py-3 rounded text-sm whitespace-nowrap inline-flex items-center gap-2 flex-shrink-0"
          >
            REQUEST CONSULTATION <ArrowRight className="w-4 h-4" />
          </a>
        </div>

        {/* Info cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {[
            { icon: Mail, title: "Email", content: "info@apollocompounds.com", sub: "Product inquiries & support" },
            { icon: MapPin, title: "Location", content: "United States", sub: "US-based operations & shipping" },
            { icon: Clock, title: "Response Time", content: "24-48 Hours", sub: "Business days, Mon-Fri" },
          ].map(({ icon: Icon, title, content, sub }) => (
            <div
              key={title}
              className="neon-border rounded-lg p-6 text-center card-hover"
              style={{ background: 'var(--bg-surface)' }}
            >
              <Icon className="w-6 h-6 mx-auto mb-3" style={{ color: 'var(--neon-cyan)' }} />
              <h3 className="text-sm font-bold mb-1" style={{ fontFamily: "'Rajdhani', sans-serif" }}>{title}</h3>
              <p className="text-sm mb-1" style={{ color: 'var(--neon-cyan)', fontFamily: "'Roboto Mono', monospace", fontSize: '12px' }}>{content}</p>
              <p className="text-[11px]" style={{ color: 'var(--text-secondary)' }}>{sub}</p>
            </div>
          ))}
        </div>

        {/* What to include */}
        <div
          className="neon-border rounded-lg p-8"
          style={{ background: 'var(--bg-surface)' }}
        >
          <h3 className="text-lg font-bold mb-4" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
            What to Include in Your Email
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              "Your name and institution/organization",
              "Compound(s) of interest and desired quantity (mg)",
              "Intended research application",
              "Shipping address (US domestic preferred)",
              "Any specific purity or COA requirements",
              "Timeline for your research needs",
            ].map((item) => (
              <div key={item} className="flex items-start gap-3 text-sm" style={{ color: 'var(--text-secondary)' }}>
                <span className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0" style={{ background: 'var(--neon-cyan)' }} />
                {item}
              </div>
            ))}
          </div>
        </div>

        <Separator className="my-12" style={{ backgroundColor: 'rgba(0,240,255,0.08)' }} />

        {/* Disclaimer */}
        <div
          className="neon-border-magenta rounded-lg p-6 text-center"
          style={{ background: 'var(--bg-surface)' }}
        >
          <Shield className="w-5 h-5 mx-auto mb-3" style={{ color: 'var(--neon-magenta)' }} />
          <p
            className="text-[11px] tracking-[0.1em] uppercase mb-2 font-semibold"
            style={{ color: 'var(--neon-magenta)', fontFamily: "'Roboto Mono', monospace" }}
          >
            Important Notice
          </p>
          <p className="text-xs leading-relaxed max-w-2xl mx-auto" style={{ color: 'var(--text-secondary)' }}>
            All products sold by Apollo Compounds are intended for laboratory and research use only. Not for human or animal consumption, therapeutic, or diagnostic purposes.
          </p>
        </div>

        {/* Browse CTA */}
        <div className="text-center mt-12">
          <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
            Not sure which compound you need? Browse our full catalog first.
          </p>
          <Link to="/products" data-testid="contact-browse-btn" className="btn-ghost-neon px-8 py-3 rounded text-sm inline-flex items-center gap-2">
            VIEW CATALOG <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}
